import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDictList } from '@/utils/api'

/**
 * 数据字典Pinia Store
 * 用于管理系统中所有的数据字典数据，支持缓存、自动刷新、类型安全等特性
 */
export const useDictionaryStore = defineStore('dictionary', () => {
  // 存储键名
  const STORAGE_KEY = 'dictionary_cache'

  // 响应式状态
  const dictionaries = ref({}) // 存储所有字典数据
  const loading = ref(false) // 加载状态
  const lastUpdated = ref({}) // 每个字典的最后更新时间

  // 从本地存储加载缓存数据
  const loadFromStorage = () => {
    try {
      const cachedData = localStorage.getItem(STORAGE_KEY)
      if (cachedData) {
        const parsedData = JSON.parse(cachedData)
        const { data } = parsedData
        
        dictionaries.value = data
        lastUpdated.value = Object.keys(data).reduce((acc, key) => {
          acc[key] = Date.now()
          return acc
        }, {})
        return true
      }
    } catch (error) {
      console.error('加载字典缓存失败:', error)
    }
    return false
  }

  // 保存到本地存储
  const saveToStorage = () => {
    try {
      const dataToSave = {
        data: dictionaries.value,
        timestamp: Date.now()
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
    } catch (error) {
      console.error('保存字典缓存失败:', error)
    }
  }

  // 计算属性 - 获取所有字典类型
  const dictionaryTypes = computed(() => Object.keys(dictionaries.value))

  // 计算属性 - 获取加载状态
  const isLoading = computed(() => loading.value)

  // 获取指定字典类型的数据
  const getDictionaryByType = computed(() => (type) => {
    return dictionaries.value[type] || []
  })

  // 获取字典项的显示文本
  const getDictLabel = computed(() => (type, value) => {
    const dictItems = dictionaries.value[type] || []
    const item = dictItems.find(item => item.value === value || item.id === value)
    return item ? item.label || item.name : value
  })

  // 获取字典项的值
  const getDictValue = computed(() => (type, label) => {
    const dictItems = dictionaries.value[type] || []
    const item = dictItems.find(item => item.label === label || item.name === label)
    return item ? item.value || item.id : label
  })

  // Actions

  /**
   * 获取字典数据
   * @param {string} type - 字典类型
   * @param {boolean} forceRefresh - 是否强制刷新
   */
  async function fetchDictionary(type, forceRefresh = false) {
    // 检查是否需要刷新
    if (!forceRefresh && dictionaries.value[type]) {
      return dictionaries.value[type]
    }

    loading.value = true
    try {
      const response = await getDictList(type)
      if (response.data && response.data.items) {
        dictionaries.value[type] = response.data.items
        lastUpdated.value[type] = Date.now()
        saveToStorage()
        return response.data.items
      }
      throw new Error('获取字典数据失败')
    } catch (error) {
      console.error(`获取字典 ${type} 失败:`, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * 批量获取多个字典数据
   * @param {string[]} types - 字典类型数组
   * @param {boolean} forceRefresh - 是否强制刷新
   */
  async function fetchMultipleDictionaries(types, forceRefresh = false) {
    loading.value = true
    try {
      const promises = types.map(type => fetchDictionary(type, forceRefresh))
      await Promise.all(promises)
    } catch (error) {
      console.error('批量获取字典失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * 预加载常用字典
   */
  async function preloadCommonDictionaries() {
    const commonTypes = ['activity_type', 'activity_status']
    return fetchMultipleDictionaries(commonTypes, true)
  }

  /**
   * 清除指定字典的缓存
   * @param {string} type - 字典类型
   */
  function clearDictionaryCache(type) {
    if (type) {
      delete dictionaries.value[type]
      delete lastUpdated.value[type]
    } else {
      dictionaries.value = {}
      lastUpdated.value = {}
    }
    saveToStorage()
  }

  /**
   * 清除所有缓存（包括本地存储）
   */
  function clearAllCache() {
    dictionaries.value = {}
    lastUpdated.value = {}
    localStorage.removeItem(STORAGE_KEY)
  }

  // 初始化时尝试加载缓存
  if (!loadFromStorage()) {
    // 如果没有缓存，预加载常用字典
    preloadCommonDictionaries().catch(console.error)
  }

  return {
    // 状态
    dictionaries,
    loading,
    lastUpdated,
    
    // 计算属性
    dictionaryTypes,
    isLoading,
    getDictionaryByType,
    getDictLabel,
    getDictValue,
    
    // Actions
    fetchDictionary,
    fetchMultipleDictionaries,
    preloadCommonDictionaries,
    clearDictionaryCache,
    clearAllCache
  }
})